import React, { useEffect, useMemo, useRef } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { shallowEqual, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import InfoIcon from '@mui/icons-material/Info';
import OutlinedSelect from '../../../../common/OutlinedSelect';

import CustomPanel from '../../../../common/CustomPanel';
import { checkEmptyVTValue, isEmpty, usePrevious } from '../../../../../utils/CommonUtils';
import { PERSONAL_ACCOUNT_MESSAGE } from '../../../../legal.messages';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
  },
  iframe: {
    width: '100%',
    minHeight: 640,
    border: 0,
  },
  select: {
    margin: theme.spacing(1),
    minWidth: '95%',
  },
}));

const setRefundForm = (insuranceApplicationRefund, setValue) => {
  setValue('refund.accountNumber', insuranceApplicationRefund?.AccountNumber.trim() || '');
  setValue('refund.financialInstitution', insuranceApplicationRefund?.FinancialInstitution.trim() || '');
  setValue('refund.accountType', insuranceApplicationRefund?.AccountType.trim() || '');
};


const AccountRefunds = ({ readOnly, withDifferentHolder = false }) => {
  const classes = useStyles();
  const { control, formState: { errors }, setValue } = useFormContext();
  const { response: { catalogs = [] } } = useSelector((state) => state.catalogs, shallowEqual);
  const getApplication = useSelector((state) => state.getApplication);
  const prevGetApplication = usePrevious(getApplication);

  const financialInstitution = catalogs && catalogs.find((c) => c.id === 'InstitucionFinancieraReembolso')?.catalogue;
  const financialInstitutionOptions = useMemo(() => {
    if (!financialInstitution?.length) {
      return [];
    }
    const seenIds = new Set();
    return financialInstitution.filter((item) => {
      if (seenIds.has(item.id)) {
        return false;
      }
      seenIds.add(item.id);
      return true;
    });
  }, [financialInstitution]);
  const clientInfo = useSelector((state) => state.clientInfo);

  const prevClientInfo = usePrevious(clientInfo);
  const findByIdentificationResult = useSelector((state) => state.findByIdentificationResult);

  useEffect(() => {
    if (prevClientInfo && (clientInfo.response !== prevClientInfo.response)) {
      if (!withDifferentHolder) {
        setClientInformation(clientInfo.response.clientDto);
      }
    }
  }, [clientInfo.response]);

  useEffect(() => {
    if (prevGetApplication && (prevGetApplication.response !== getApplication.response)) {
      if (!isEmpty(getApplication.response)) {
        setRefundForm(getApplication.response.InsuranceApplicationRefund, setValue);
      }
    }
  }, [getApplication, prevGetApplication]);

  const setClientInformation = (client) => {
    setValue('refund.accountType', client?.nAccType?.toString() || '');
    setValue('refund.accountNumber', client?.sAccount || '');
    setValue('refund.financialInstitution', checkEmptyVTValue(client?.nBankCode));
  };

  useEffect(() => {
    if (findByIdentificationResult.found) {
      setClientInformation(findByIdentificationResult.response.clientDto);
    }
  }, [findByIdentificationResult.response]);

  const numeroCuentaRef = useRef();
  const bancoRef = useRef();

  return (
    <CustomPanel title="Cuenta Reembolsos" border={false}>
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={3}
      >
        <Grid item md={6} xs={12}>
          <Controller
            name="refund.accountNumber"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                inputRef={numeroCuentaRef}
                onFocus={() => numeroCuentaRef.current.focus()}
                onKeyPress={(e) => {
                  const key = e.which;
                  if (!(key >= 48 && key <= 57)) {
                    e.preventDefault();
                  }
                }}
                fullWidth
                label="NÚMERO DE CUENTA"
                autoComplete="off"
                error={errors.refund?.hasOwnProperty('accountNumber')}
                helperText={errors.refund?.accountNumber && errors.refund?.accountNumber.message}
                InputProps={{ readOnly }}
              />
            )}
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <FormControl
            className={classes.select}
            error={errors.refund?.financialInstitution && true}
          >
            <InputLabel id="financial-institution-select">INSTITUCIÓN FINANCIERA</InputLabel>
            <Controller
              name="refund.financialInstitution"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <OutlinedSelect {...field} labelId="financial-institution-select" label="INSTITUCIÓN FINANCIERA" inputRef={bancoRef} onFocus={() => bancoRef.current.focus()} inputProps={{ readOnly }}>
                  <MenuItem value="">
                    <em>Seleccione..</em>
                  </MenuItem>
                  {financialInstitutionOptions.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.description}
                    </MenuItem>
                  ))}
                </OutlinedSelect>
              )}
            />
            {errors.refund?.financialInstitution
            && (
              <FormHelperText>
                {errors.refund?.financialInstitution.message}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item md={12} xs={12}>
          <FormControl component="fieldset" error={errors.refund?.accountType && true}>
            <Controller
              name="refund.accountType"
              control={control}
              defaultValue="1"
              render={({ field }) => (
                <RadioGroup {...field} row className={classes.checkBoxes}>
                  <FormControlLabel
                    value="1"
                    control={<Radio disabled={readOnly} />}
                    label="Ahorros"
                  />
                  <FormControlLabel
                    value="2"
                    control={<Radio disabled={readOnly} />}
                    label="Corriente"
                  />
                </RadioGroup>
              )}
            />
            {errors.refund?.accountType
            && (
              <FormHelperText>
                {errors.refund?.accountType.message}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item md={12} xs={12}>

          <IconButton edge="start" size="large">
            <InfoIcon style={{ color: '#e53935' }} />
          </IconButton>
          &nbsp;
          {PERSONAL_ACCOUNT_MESSAGE}
        </Grid>
      </Grid>
    </CustomPanel>
  );
};

AccountRefunds.propTypes = {
  readOnly: PropTypes.bool.isRequired,
  withDifferentHolder: PropTypes.bool,
};

export default AccountRefunds;
